import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useSystemId } from "../../../hooks/useReduxSelectors";
import {
  selectDevicesFilterList,
  selectFormattedEntries,
} from "../../../ducks/selectors";
import {
  removeChartLine,
  removeDeviceFilter,
  removeDeviceFilterList,
  setDevicesFilter,
} from "../../../ducks/trendsDuck";
import {
  Box,
  CircularProgress,
  Select,
  MenuItem,
  ListItemIcon,
  Typography,
} from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ClearIcon from "@mui/icons-material/Clear";
import { kontram } from "../../../../theme/muiTheme";

const CHARTS = ["chart1", "chart2", "chart3", "chart4"];

const ModuleFilterItem = React.memo(({ hwId, selectedItem, isLast }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const systemId = useSystemId();
  const { valueName } = selectedItem;

  // ─── Targeted selectors: only re-render when THIS item's data changes ──────

  const itemValue = useSelector(
    (state) =>
      selectFormattedEntries(state, systemId)?.[hwId]?.[valueName]?.value,
  );
  const itemUnit = useSelector(
    (state) =>
      selectFormattedEntries(state, systemId)?.[hwId]?.[valueName]?.unit,
  );

  const isLoading = useSelector((state) => {
    const dfl = selectDevicesFilterList(state, systemId);
    for (const c of CHARTS) {
      if (dfl?.[c]?.[hwId]?.[valueName]?.loading) return true;
    }
    return false;
  });

  const hasError = useSelector((state) => {
    const dfl = selectDevicesFilterList(state, systemId);
    for (const c of CHARTS) {
      if (dfl?.[c]?.[hwId]?.[valueName]?.fetchErrorMsg) return true;
    }
    return false;
  });

  // Encode chart selection state as a single string for stable === comparison.
  // Format: "chart1:color1:err1|chart2:color2:err2" — only changes when this item's
  // selection/color/error actually changes. Avoids new object references.
  const chartSelectionsKey = useSelector((state) => {
    const dfl = selectDevicesFilterList(state, systemId);
    if (!dfl) return "";
    const parts = [];
    for (const c of CHARTS) {
      const val = dfl[c]?.[hwId]?.[valueName];
      if (val?.isChecked)
        parts.push(`${c}:${val.color || ""}:${val.fetchErrorStatus || ""}`);
    }
    return parts.join("|");
  });

  const chartSelections = useMemo(() => {
    if (!chartSelectionsKey) return [];
    return chartSelectionsKey.split("|").map((part) => {
      const [chart, color, fetchError] = part.split(":");
      return { chart, color, fetchError };
    });
  }, [chartSelectionsKey]);

  // Click handler reads current state via store.getState() — no subscription needed
  const onDropdownItemClick = useCallback(
    (chart) => {
      const state = store.getState();
      const dfl = selectDevicesFilterList(state, systemId);
      const exists = dfl?.[chart]?.[hwId]?.[valueName];

      if (exists) {
        dispatch(
          removeDeviceFilter({
            systemId,
            selectedItem,
            hwId,
            selectedChart: chart,
          }),
        );
        dispatch(removeDeviceFilterList({ systemId, hwId, valueName }));
        dispatch(
          removeChartLine({ selectedChart: chart, systemId, valueName, hwId }),
        );
      } else {
        const fe = selectFormattedEntries(state, systemId);
        const entry = fe?.[hwId]?.[valueName];
        const { flag, unit } = entry || {};
        dispatch(
          setDevicesFilter({
            systemId,
            selectedItem,
            hwId,
            flag,
            unit,
            selectedChart: chart,
          }),
        );
      }
    },
    [store, systemId, dispatch, hwId, selectedItem, valueName],
  );

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        display: "grid",
        gridTemplateColumns: "1fr 90px 70px",
        alignItems: "center",
        gap: 1,
        borderBottom: "3px solid",
        borderColor: "divider",
        p: 0.5,
        pl: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          gap: 0,
          minWidth: 0,
        }}
      >
        <Typography sx={{ fontSize: "0.8rem" }}>{selectedItem.name}</Typography>
        <Typography sx={{ display: "flex", alignItems: "center" }}>
          {itemValue} {itemUnit}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {isLoading && <CircularProgress size={20} />}
        {hasError && <Typography color="error">Load Error!</Typography>}
      </Box>
      <Select
        size="small"
        value=""
        displayEmpty
        IconComponent={() => null}
        sx={{
          padding: "0",
          backgroundColor: hasError ? kontram.red : undefined,
          display: "flex !important",
          justifyContent: "center",
          alignItems: "center",
          width: "60px",
          minWidth: "60px",
          maxWidth: "60px",
          overflow: "hidden",
          "& .MuiSelect-select": {
            padding: "0 !important",
            display: "flex !important",
            justifyContent: "center !important",
            alignItems: "center !important",
            minWidth: 0,
            overflow: "hidden",
          },
        }}
        renderValue={() => {
          if (chartSelections.length === 0) {
            return <ShowChartIcon fontSize="small" />;
          }

          const indices = chartSelections.map((sel) =>
            parseInt(sel.chart.replace("chart", "")),
          );

          const primaryColor = chartSelections[0]?.color || undefined;
          const borderColor = hasError ? "red" : primaryColor;

          // Always render as a 2-col grid enclosed by a single outer border,
          // regardless of selection count, so 1/2/3/4 selections all look unified.
          return (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 0,
                fontSize: "0.75rem",
                border: "1px solid",
                borderColor,
                borderRadius: "4px",
                overflow: "hidden",
                color: hasError ? "white" : primaryColor,
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              {indices.map((idx, cellIdx) => {
                const hasRightNeighbor = cellIdx % 2 === 0 && cellIdx + 1 < indices.length;
                const hasBottomNeighbor = cellIdx + 2 < indices.length;
                return (
                  <Box
                    key={idx}
                    sx={{
                      textAlign: "center",
                      padding: "2px 4px",
                      minWidth: 0,
                      overflow: "hidden",
                      borderColor,
                      borderStyle: "solid",
                      borderWidth: "0",
                      borderRightWidth: hasRightNeighbor ? "1px" : 0,
                      borderBottomWidth: hasBottomNeighbor ? "1px" : 0,
                    }}
                  >
                    {idx}
                  </Box>
                );
              })}
            </Box>
          );
        }}
      >
        {CHARTS.map((chart, index) => {
          const sel = chartSelections.find((s) => s.chart === chart);
          const primaryColor =
            chartSelections.length > 0 ? chartSelections[0]?.color : undefined;
          return (
            <MenuItem
              key={chart}
              style={{
                color: sel?.fetchError ? "red" : primaryColor || undefined,
              }}
              onClick={() => onDropdownItemClick(chart)}
            >
              <ListItemIcon>
                {sel?.color === undefined ? (
                  <ShowChartIcon
                    fontSize="small"
                    style={{
                      color: sel?.fetchError
                        ? "red"
                        : primaryColor || undefined,
                    }}
                  />
                ) : (
                  <ClearIcon
                    fontSize="small"
                    style={{
                      color: sel?.fetchError ? "red" : "",
                    }}
                  />
                )}
              </ListItemIcon>
              {`Trend ${index + 1}`}
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
});

export default ModuleFilterItem;
